import { useState } from 'react';
import { useLocation, useRevalidator } from 'react-router';

import { Box } from '#/components/Box/Box';
import { FloatingImageHolder } from '#/components/FloatingImageHolder/FloatingImageHolder';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { H4 } from '#/components/Text/H4';
import { Text } from '#/components/Text/Text';
import { useAppConfig } from '#/config/AppConfigContext';
import { EmailSignupFormContainer } from '#/containers/EmailSignupFormContainer';
import { LoginFormContainer } from '#/containers/LoginFormContainer';
import { useI18n } from '#/i18n/hooks/useI18n';
import { salariesFaqRoute } from '#/modules/salary/routing/salary.routes';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import {
  SalariesExploreAuthPageBody,
  SalariesExploreAuthPageHolder,
  SalariesExploreAuthPageSidebar,
  SalariesExploreAuthPageSidebarImageHolder,
} from './SalariesExplorePageAuth.styled';

export function SalariesExplorePageAuth() {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();
  const [signupMode, setSignupMode] = useState(true);
  const location = useLocation();
  const redirectTo = `${location.pathname}${location.search}`;
  const revalidator = useRevalidator();

  return (
    <SalariesExploreAuthPageHolder>
      <SalariesExploreAuthPageBody>
        <Box mx="auto" pt={36} maxWidth={480} width={480}>
          <Box textAlign="center">
            <IdealistLogo height={82} />
          </Box>
          <H4 mb="0.5em" textAlign="center" mt={1}>
            {i18n('Sign up or log in to see what you could be earning!')}
          </H4>

          <Text styleAs="bodySmall" mb="1em" textAlign="center">
            {i18n('Powered by Idealist’s database of nonprofit salaries.')}
          </Text>

          {signupMode && (
            <EmailSignupFormContainer
              analyticsTitle="[main] Salary report page - signup mode"
              usageContext="page"
              loginOverrideFn={() => {
                setSignupMode(false);
              }}
              redirectTo={redirectTo}
              onSignup={() => {
                void revalidator.revalidate();
              }}
            />
          )}

          {!signupMode && (
            <LoginFormContainer
              analyticsTitle="[main] Salary report page"
              usageContext="page"
              signUpOverrideFn={() => {
                setSignupMode(true);
              }}
              redirectTo={redirectTo}
              email={null}
              inviteId={null}
              initialWarning={null}
              onLogin={() => {
                void revalidator.revalidate();
              }}
            />
          )}
        </Box>

        <Box mt={72}>
          <Box mx="auto" maxWidth={480}>
            <Text styleAs="bodySmall" mt="1em">
              <Text styleAs="h5" tag="span">
                {
                  /* TRANSLATORS: Salary explorer footer */ i18n(
                    'We take data security very seriously. ',
                  )
                }
              </Text>
              <span>
                {i18n(`The information you enter in the survey will not be linked to your
          user account, and will be completely anonymized. `)}
              </span>
              {
                /* TRANSLATORS: Salary explorer footer */ i18n(
                  'Want to know more? ',
                )
              }
              {
                /* TRANSLATORS: Salary explorer footer */ i18n(
                  `{{link}}Learn more in our FAQ{{/link}} about how we protect your privacy.`,
                  {
                    link: (
                      <StyledNativeLinkWithRef
                        target="_blank"
                        href={salariesFaqRoute.fullPath}
                      />
                    ),
                  },
                )
              }
            </Text>
          </Box>
        </Box>
      </SalariesExploreAuthPageBody>

      <SalariesExploreAuthPageSidebar>
        <SalariesExploreAuthPageSidebarImageHolder>
          <FloatingImageHolder>
            <img
              src={getStaticImageSrc(
                'compdb/survey-step1.svg',
                appConfig.idealist.staticImgPath,
              )}
              width={270}
              height={282}
              alt=""
            />
          </FloatingImageHolder>
        </SalariesExploreAuthPageSidebarImageHolder>
      </SalariesExploreAuthPageSidebar>
    </SalariesExploreAuthPageHolder>
  );
}
